/**
 * errors.ts — the fleet's shared error→message helpers for client-facing apps.
 *
 * Guarantees that no raw HTTP status code ("HTTP 500", "FastAPI error 503",
 * "esl_api error 503", "Upload failed (413)") ever reaches the rendered DOM.
 * Every fetch failure degrades to a branded, human message. Pair with
 * `<ErrorState>` (`@plerity/ui-core/ErrorState`) for a retry affordance —
 * Client Platform Build Standard item 1 ("error ≠ empty").
 *
 * Lifted 2026-10-07 from three byte-near-identical copies
 * (medspa, medspa-patient, esl-patient `src/lib/errors.ts`) — backlog
 * `lift-2026-09-30-001` (b). Those app files are now thin re-exports so the
 * existing `@lib/errors` imports keep working.
 *
 * Framework-agnostic (no React import) so Astro server endpoints and plain TS
 * modules can use it too.
 */

export const DEFAULT_ERROR_MESSAGE = "Something went wrong. Please try again.";

/** Map an HTTP status code to a branded, human-readable message. */
export function friendlyHttpMessage(status: number, fallback: string = DEFAULT_ERROR_MESSAGE): string {
  if (!status || status === 0) {
    return "Can't reach the server. Check your connection and try again.";
  }
  if (status === 401 || status === 403) {
    return "Your session has expired. Please sign in again.";
  }
  if (status === 404) {
    return "We couldn't find what you were looking for.";
  }
  if (status === 408 || status === 429) {
    return "The service is busy right now. Please wait a moment and try again.";
  }
  if (status >= 500) {
    return "The server ran into a problem. Please try again in a moment.";
  }
  // Any other 4xx without a server-supplied message.
  return fallback;
}

/**
 * Patterns that indicate a raw status code leaked into a message string.
 * If any match, the message is swapped for a branded fallback so the user
 * never sees "HTTP 500" / "FastAPI error 503" / "Upload failed (413)".
 * `error\s+\d{3}` also covers any "<backend> error 503" prefix
 * (`FastAPI error 503`, `esl_api error 503`).
 */
export const RAW_STATUS_PATTERNS: readonly RegExp[] = [
  /\bHTTP\s*\d{3}\b/i,
  /\bFastAPI\s+error\s*\d{3}\b/i,
  /\(\s*\d{3}\s*\)/, // "Upload failed (413)"
  /\b(?:status|code)\s*[:=]?\s*\d{3}\b/i,
  /^\s*\d{3}\s*\.?\s*$/, // bare "500" / "500."
  /\berror\s+\d{3}\b/i,
];

/** True if `message` looks like it exposes a raw HTTP status code. */
export function looksLikeRawStatus(message: string): boolean {
  return RAW_STATUS_PATTERNS.some((re) => re.test(message));
}

function humanizeField(name: string): string {
  const spaced = name.replace(/_/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Extract the human message from a JSON error body. FastAPI backends proxied
 * verbatim surface their own shapes — `{detail: "Product not found"}`
 * (HTTPException) or `{detail: [{loc, msg}]}` (422 validation) — not only the
 * `{success:false, error}` envelope. Reading only `error` turned every 422
 * into a generic "Failed to save" (medspa task 2245).
 * Returns null when the body carries nothing usable (caller picks fallback).
 */
export function serverErrorMessage(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  const b = body as { error?: unknown; detail?: unknown; message?: unknown };
  if (typeof b.error === "string" && b.error.trim()) return b.error;
  if (typeof b.detail === "string" && b.detail.trim()) return b.detail;
  if (Array.isArray(b.detail) && b.detail.length > 0) {
    const parts = b.detail
      .map((d) => {
        if (!d || typeof d !== "object") return null;
        const item = d as { loc?: unknown; msg?: unknown };
        const msg = typeof item.msg === "string" ? item.msg : null;
        if (!msg) return null;
        const loc = Array.isArray(item.loc) ? item.loc.filter((p) => p !== "body" && p !== "query") : [];
        const field = loc.length > 0 ? humanizeField(String(loc[loc.length - 1])) : null;
        return field ? `${field}: ${msg}` : msg;
      })
      .filter((p): p is string => !!p);
    if (parts.length > 0) return parts.join("; ");
  }
  if (typeof b.message === "string" && b.message.trim()) return b.message;
  return null;
}

interface StatusCarrier {
  status?: number;
}

/**
 * Convert any thrown value into a branded user-facing message — never a raw
 * status code. Resolution order:
 *   1. If the error carries a numeric `status` AND its own message looks like
 *      a raw-status leak → `friendlyHttpMessage(status)`.
 *   2. A clean human message already on the error → use it verbatim.
 *   3. The caller's branded `fallback`.
 */
export function toUserMessage(error: unknown, fallback: string = DEFAULT_ERROR_MESSAGE): string {
  if (error == null) return fallback;

  const status =
    typeof error === "object" && typeof (error as StatusCarrier).status === "number"
      ? (error as StatusCarrier).status
      : undefined;

  const rawMessage = error instanceof Error ? error.message : typeof error === "string" ? error : "";

  if (!rawMessage || looksLikeRawStatus(rawMessage)) {
    return typeof status === "number" ? friendlyHttpMessage(status, fallback) : fallback;
  }

  return rawMessage;
}
